import React, { useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faSquareArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import RethinkSyncModalStart from './modal_start';
import RethinkSyncModalExporting from './modal_exporting';
import RethinkSyncModalComplete from './modal_complete';
import { getCallList } from '../../../helpers/prospect_api';
import { type RethinkCallListSync, type CallList } from 'bundles/connect/types';
import RethinkSyncModalContactUnlocking from './modal_contact_unlocking';
import { rethinkListPath } from '../../../helpers/rethink_helper';

type Props = {
  callListId: number,
  callListName: string,
  initialRethinkListId?: string,
  initialRethinkSync?: RethinkCallListSync,
  rethinkInstanceUrl: string
}


export default function CallListRethinkSync(
  { callListId, callListName, initialRethinkListId, initialRethinkSync, rethinkInstanceUrl }: Props
) {

  const [showModal, setShowModal] = useState(false);
  const [rethinkSync, setRethinkSync] = useState<RethinkCallListSync | undefined>(initialRethinkSync);
  const [rethinkListId, setRethinkListId] = useState<string | undefined>(initialRethinkListId);
  const [syncStep, setSyncStep] = useState(1);

  const modelBodyContent = () => {
    switch (syncStep) {
    case 1:
      return (
        <RethinkSyncModalStart
          callListId={callListId}
          initialCallListName={callListName}
          show={showModal}
          onExportStarted={handleSyncStarted}
          onHide={() => setShowModal(false)}
        />
      );
    case 2:
      return (
        <RethinkSyncModalContactUnlocking
          callListId={callListId}
          onHide={() =>setShowModal(false)}
          onOwnerUnlockComplete={handleOwnerUnlockComplete}
        />
      );
    case 3:
      if (!rethinkSync) return null;

      return (
        <RethinkSyncModalExporting
          rethinkSync={rethinkSync}
          onHide={() => setShowModal(false)}
          onSyncComplete={handleSyncComplete}
        />
      );
    case 4:
      if (!rethinkListId) return null;

      return (
        <RethinkSyncModalComplete
          rethinkInstanceUrl={rethinkInstanceUrl}
          rethinkListId={rethinkListId}
          onHide={() => setShowModal(false)}
        />
      );
    }
  };

  const handleSyncStarted = (sync: RethinkCallListSync) => {
    setRethinkSync(sync);
    if (sync) {
      setSyncStep(2);
    } else {
      setSyncStep(3);
    }
  };

  const handleOwnerUnlockComplete = () => {
    setSyncStep(3);
  };

  const handleSyncComplete = () => {
    getCallList(callListId).then((callList: CallList) => {
      setRethinkListId(callList.rethinkId);
      setSyncStep(4);
    });
  };

  const renderExportButton = () => {
    if (rethinkListId) {
      return (
        <Button
          className="d-flex align-items-center gap-2"
          href={rethinkListPath(rethinkInstanceUrl, rethinkListId)}
          rel="noopener noreferrer"
          target="_blank"
          variant="link"
        >
          <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
          <span>View in Rethink</span>
        </Button>
      );
    } else if (rethinkSync?.uploadError) {
      return (
        <Button className="text-danger" disabled variant="link">
          Error exporting
        </Button>
      );
    } else if (rethinkSync?.syncProgress !== undefined && rethinkSync?.syncProgress !== 100) {
      return (
        <Button className="text-muted" disabled variant="link">
          <Spinner animation="border" size="sm" />
          Exporting
        </Button>
      );
    } else {
      return (
        <Button variant="link" onClick={() => setShowModal(true)}>
          <FontAwesomeIcon icon={faSquareArrowUpRight}/>
          Export to Rethink
        </Button>
      );
    }
  };
  

  return (
    <>
      {renderExportButton()}
      <Modal centered show={showModal} onHide={() => setShowModal(false)}>
        {modelBodyContent()}  
      </Modal>
    </>
  );
}
